import React, { useEffect, useState } from 'react';
import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import Divider from '@mui/joy/Divider';
import Chip from '@mui/joy/Chip';
import CircularProgress from '@mui/joy/CircularProgress';
import { ArrowBendUpLeft as ReplyIcon, CaretDown, CaretUp } from '@phosphor-icons/react';
import { format } from 'date-fns';
import type { InboxItem, EmailThread, EmailMessage } from '../../types/inbox';
import { EmailBodyViewer } from './EmailBodyViewer';
import { EmailReplyComposer } from './EmailReplyComposer';
import api from '../../lib/api';

interface InlineEmailViewProps {
  item: InboxItem;
  /** @deprecated replies now send directly via EmailReplyComposer */
  onReply?: (content: string, type: 'email' | 'sms') => void;
  onReplyStateChange?: (isReplying: boolean) => void;
}

/** One message inside the expanded thread: header row + body on demand. */
const ThreadMessage: React.FC<{ message: EmailMessage; defaultExpanded: boolean }> = ({
  message,
  defaultExpanded,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const isInbound = message.direction === 'inbound';
  const sender = message.from_display || message.from_address;

  return (
    <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 1, mb: 1 }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        onClick={() => setExpanded(v => !v)}
        sx={{ cursor: 'pointer', py: 0.5 }}
      >
        <Chip size="sm" variant="soft" color={isInbound ? 'primary' : 'success'}>
          {isInbound ? 'Received' : 'Sent'}
        </Chip>
        <Typography level="body-sm" fontWeight="md" sx={{ flex: 1, minWidth: 0 }} noWrap>
          {isInbound ? sender : `To: ${message.to_addresses.join(', ')}`}
        </Typography>
        <Typography level="body-xs" color="neutral">
          {message.sent_at ? format(new Date(message.sent_at), 'MMM d, h:mm a') : ''}
        </Typography>
        {expanded ? <CaretUp size={14} /> : <CaretDown size={14} />}
      </Stack>
      {expanded ? (
        <EmailBodyViewer messageId={message.id} />
      ) : (
        <Typography level="body-xs" color="neutral" noWrap>
          {message.snippet}
        </Typography>
      )}
    </Box>
  );
};

const InlineEmailView: React.FC<InlineEmailViewProps> = ({ item, onReplyStateChange }) => {
  const [showReply, setShowReply] = useState(false);
  const [thread, setThread] = useState<EmailThread | null>(null);
  const [loading, setLoading] = useState(true);

  // source_id format: "thread-{id}" (backend contract)
  const threadId = item.source_id?.startsWith('thread-')
    ? item.source_id.replace('thread-', '')
    : null;

  const loadThread = () => {
    if (!threadId) {
      setLoading(false);
      return;
    }
    api.get<EmailThread>(`/api/inbox/email/${threadId}/`)
      .then(response => setThread(response.data))
      .catch(() => { /* fall back to the preview text below */ })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadThread();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  const handleShowReply = () => {
    setShowReply(true);
    onReplyStateChange?.(true);
  };

  const handleHideReply = () => {
    setShowReply(false);
    onReplyStateChange?.(false);
  };

  const messages = thread?.messages ?? [];
  const lastInbound = [...messages].reverse().find(m => m.direction === 'inbound');
  const replySubject = thread?.subject
    ? (/^re:/i.test(thread.subject) ? thread.subject : `Re: ${thread.subject}`)
    : `Re: ${item.subject}`;
  const replyTo = lastInbound
    ? [lastInbound.from_address]
    : (item.from_email ? [item.from_email] : []);

  return (
    <Box sx={{ p: 3 }}>
      {/* Email Header */}
      <Stack spacing={1} sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1}>
          <Typography level="body-sm" fontWeight="md">From:</Typography>
          <Typography level="body-sm">
            {item.from_email
              ? `${item.lead_name || 'Unknown'} <${item.from_email}>`
              : (item.lead_name || 'Unknown')}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Typography level="body-sm" fontWeight="md">To:</Typography>
          <Typography level="body-sm">{item.to_email || 'Me'}</Typography>
        </Stack>
      </Stack>

      <Divider sx={{ my: 2 }} />

      {/* Thread — envelopes always, bodies fetched when a message is opened */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size="sm" />
        </Box>
      ) : messages.length > 0 ? (
        <Box sx={{ maxHeight: '420px', overflowY: 'auto', pr: 0.5 }}>
          {messages.map((message, index) => (
            <ThreadMessage
              key={message.id}
              message={message}
              defaultExpanded={index === messages.length - 1}
            />
          ))}
        </Box>
      ) : (
        <Box sx={{ maxHeight: '250px', overflowY: 'auto', p: 1, bgcolor: 'background.level1', borderRadius: 'sm' }}>
          <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap' }}>
            {item.preview}
          </Typography>
        </Box>
      )}

      {/* Reply Section */}
      {!showReply ? (
        <Box sx={{ mt: 3 }}>
          <Button
            variant="soft"
            size="sm"
            startDecorator={<ReplyIcon />}
            onClick={handleShowReply}
          >
            Reply
          </Button>
        </Box>
      ) : threadId ? (
        <Box sx={{ mt: 3 }}>
          <EmailReplyComposer
            threadId={Number(threadId)}
            defaultTo={replyTo}
            defaultSubject={replySubject}
            contactName={item.lead_name}
            onSent={() => {
              handleHideReply();
              loadThread();
            }}
            onCancel={handleHideReply}
          />
        </Box>
      ) : null}
    </Box>
  );
};

export default InlineEmailView;
